import { z } from 'zod';
import { TextKey } from '@mews-commander/enums';
import { CommonProcessPaymentFormSchema, ProcessPaymentFormField } from '../ProcessPaymentFormSchema';
import { NEW_CREDIT_CARD_VALUE } from '../../../../constants';
import { recordCardFormSchema } from '../RecordCardForm/schema';

export enum ExternalCardPaymentFormField {
    PaymentMethodId = 'paymentMethodId',
    PreAuthorizationId = 'preAuthorizationId',
    ReceiptIdentifier = 'receiptIdentifier',
}

export const externalCardPaymentBaseSchema = {
    [ProcessPaymentFormField.Amount]: z.number(),
};

export const createExternalCardPaymentForNewCardSchema = (optionalCreditCardPaymentDetailsEnabled: boolean) => {
    const recordCardSchema = CommonProcessPaymentFormSchema.merge(recordCardFormSchema);
    return recordCardSchema.extend({
        ...externalCardPaymentBaseSchema,
        [ExternalCardPaymentFormField.PaymentMethodId]: z.literal(NEW_CREDIT_CARD_VALUE),
        [ExternalCardPaymentFormField.ReceiptIdentifier]: z
            .string()
            .optional()
            .superRefine((value, ctx) => {
                if (!value && !optionalCreditCardPaymentDetailsEnabled) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.too_small,
                        type: 'string',
                        minimum: 1,
                        inclusive: true,
                    });
                }
            }),
    });
};

export const createExternalCardPaymentForSelectedCardSchema = (optionalCreditCardPaymentDetailsEnabled: boolean) =>
    CommonProcessPaymentFormSchema.extend({
        ...externalCardPaymentBaseSchema,
        [ExternalCardPaymentFormField.PaymentMethodId]: z.string(),
        [ExternalCardPaymentFormField.ReceiptIdentifier]: z
            .string()
            .optional()
            .superRefine((value, ctx) => {
                if (!value && !optionalCreditCardPaymentDetailsEnabled) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.too_small,
                        type: 'string',
                        minimum: 1,
                        inclusive: true,
                    });
                }
            }),
    });

export const createExternalCardPaymentForPreauthSchema = (preauthAmount: number) =>
    CommonProcessPaymentFormSchema.extend({
        ...externalCardPaymentBaseSchema,
        [ExternalCardPaymentFormField.PreAuthorizationId]: z.string(),
        [ExternalCardPaymentFormField.ReceiptIdentifier]: z.string().optional(),
        [ProcessPaymentFormField.Amount]: z
            .number()
            .gt(0)
            .max(preauthAmount, TextKey.ProcessPaymentMewsPreauthorizationAmountError),
    });

export type ExternalCardPaymentFieldsForNewCard = z.infer<ReturnType<typeof createExternalCardPaymentForNewCardSchema>>;
export type ExternalCardPaymentFieldsSelectedCard = z.infer<
    ReturnType<typeof createExternalCardPaymentForSelectedCardSchema>
>;
export type ExternalCardPaymentFieldsForPreauth = z.infer<ReturnType<typeof createExternalCardPaymentForPreauthSchema>>;

export type ExternalCardPaymentFormValues =
    | ExternalCardPaymentFieldsForNewCard
    | ExternalCardPaymentFieldsSelectedCard
    | ExternalCardPaymentFieldsForPreauth;
